import { useSyncExternalStore } from "react";
import { breakpoints } from "../../../../core/hooks/dimensionProvider";
import { TimeInteractionMode } from "./types";

/*
 * Small screen or a touch pointer, as a single media query. Subscribing to the
 * query instead of to the window size means the picker re-renders only when the
 * answer really flips, not on every resize event, and it also reacts when the
 * pointer type changes (a tablet docked to a mouse).
 */
const SCROLL_MODE_QUERY = `(max-width: ${
  breakpoints.tablet - 1
}px), (pointer: coarse)`;

const getMediaQueryList = (): MediaQueryList | null =>
  typeof window !== "undefined" && typeof window.matchMedia === "function"
    ? window.matchMedia(SCROLL_MODE_QUERY)
    : null;

const subscribe = (onStoreChange: () => void): (() => void) => {
  const mediaQueryList = getMediaQueryList();
  mediaQueryList?.addEventListener("change", onStoreChange);
  return () => mediaQueryList?.removeEventListener("change", onStoreChange);
};

const getSnapshot = (): boolean => getMediaQueryList()?.matches ?? false;

/** Without a window there is nothing to scroll, so the keyboard mode wins. */
const getServerSnapshot = (): boolean => false;

/**
 * Resolves how the picker is operated: `true` means the value is picked by
 * scrolling the columns and the input is read only (mobile), `false` means the
 * value is typed from the keyboard (desktop).
 */
export const useScrollMode = (
  interactionMode: TimeInteractionMode,
): boolean => {
  const prefersScrollMode = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getServerSnapshot,
  );

  if (interactionMode === "scroll") return true;
  if (interactionMode === "keyboard") return false;
  return prefersScrollMode;
};

export default useScrollMode;
